import { Component, OnInit, OnDestroy } from '@angular/core';
import { CommonModule } from '@angular/common';
import { ActivatedRoute, Router, RouterModule } from '@angular/router';
import { Subject, takeUntil, firstValueFrom } from 'rxjs';
import { MarketingService } from '../services/marketing.service';

interface UnsubscribeState {
  loading: boolean;
  success: boolean;
  error: string | null;
  message: string | null;
}

@Component({
  selector: 'app-unsubscribe',
  standalone: true,
  imports: [CommonModule, RouterModule],
  templateUrl: './unsubscribe.component.html',
  styleUrl: './unsubscribe.component.css',
})
export class UnsubscribeComponent implements OnInit, OnDestroy {
  private destroy$ = new Subject<void>();

  state: UnsubscribeState = {
    loading: true,
    success: false,
    error: null,
    message: null,
  };

  token: string | null = null;

  constructor(
    private route: ActivatedRoute,
    private router: Router,
    private marketingService: MarketingService
  ) {}

  ngOnInit(): void {
    this.route.queryParams
      .pipe(takeUntil(this.destroy$))
      .subscribe((params) => {
        this.token = params['token'];
        if (this.token) {
          this.processUnsubscribe();
        } else {
          this.state = {
            loading: false,
            success: false,
            error: 'Invalid unsubscribe link. No token provided.',
            message: null,
          };
        }
      });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  private async processUnsubscribe(): Promise<void> {
    if (!this.token) {
      return;
    }

    this.state.loading = true;
    this.state.error = null;

    try {
      const response = await firstValueFrom(
        this.marketingService.unsubscribeByToken(this.token)
      );

      this.state = {
        loading: false,
        success: true,
        error: null,
        message:
          response.message ||
          'You have been successfully unsubscribed from marketing emails.',
      };
    } catch (error: any) {
      console.error('Unsubscribe error:', error);

      let errorMessage = 'Failed to process unsubscribe request.';

      if (error.message?.includes('Invalid or expired')) {
        errorMessage =
          'This unsubscribe link is invalid or has expired. Please contact support if you continue to receive emails.';
      } else if (error.message) {
        errorMessage = error.message;
      }

      this.state = {
        loading: false,
        success: false,
        error: errorMessage,
        message: null,
      };
    }
  }

  goToHome(): void {
    this.router.navigate(['/']);
  }

  goToLogin(): void {
    this.router.navigate(['/auth/login']);
  }

  contactSupport(): void {
    this.router.navigate(['/']);
  }

  retryUnsubscribe(): void {
    if (this.token) {
      this.processUnsubscribe();
    }
  }
}
